/**
 * Переходы режима «Мои ошибки».
 *
 * Режим собирается из двух функций: `feature-my-mistakes` знает цикл, а
 * `feature-adaptive-practice` — как считается приоритет материала и как
 * итоги ложатся в mastery. Связывает их слой приложения, а не сами функции
 * (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import { applyOutcomes, priorityContext } from '@type2x/feature-adaptive-practice';
import {
  advanceMistakes,
  collectMistakeOutcomes,
  currentDrill,
  hasMistakes,
  observeStage,
  selectMistakeUnits,
  stageTask,
  startMistakesSession,
  type MistakeSnapshot,
  type MistakesSession,
  type StageTask,
} from '@type2x/feature-my-mistakes';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для режима работы над ошибками. */
export function mistakeSnapshot(workspace: Workspace): MistakeSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    units: profile.units,
    stats: profile.stats,
  };
}

/** Есть ли над чем работать в режиме «Мои ошибки». */
export function mistakesAvailable(workspace: Workspace | null): boolean {
  return workspace !== null && hasMistakes(mistakeSnapshot(workspace));
}

/** Экран режима либо null, если слабых мест не осталось. */
export function mistakesScreen(runtime: Runtime, workspace: Workspace): AppScreen | null {
  const snapshot = mistakeSnapshot(workspace);
  const profile = activeProfile(workspace);

  const units = selectMistakeUnits(
    snapshot,
    CONFIG,
    priorityContext(
      {
        language: profile.language,
        layoutId: profile.keyboard.layoutId,
        units: profile.units,
        stats: profile.stats,
        seenInControl: profile.seenInControl,
        leftHandShare: 0.5,
      },
      runtime.clock.epochMs(),
    ),
  );
  if (units.length === 0) return null;

  return {
    name: 'my-mistakes',
    session: startMistakesSession(snapshot, units),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Переводит микросессию на следующую стадию по итогам прогона. */
export function advanceStage(session: MistakesSession, run: TypingSession): MistakesSession {
  const drill = currentDrill(session);
  if (drill === null) return session;

  return advanceMistakes(session, {
    observation: observeStage(drill.word, run.keystrokeEvents, run.typingErrors),
    metrics: run.metrics,
    errors: run.typingErrors,
  });
}

/** Задание текущей стадии либо null, если микросессия закончена. */
export function currentStageTask(session: MistakesSession): StageTask | null {
  const drill = currentDrill(session);
  return drill === null ? null : stageTask(drill);
}

/**
 * Завершает микросессию: обновляет mastery, начисляет XP и собирает итог.
 *
 * Итог собирается так же, как после тренировки: пользователю важен один
 * понятный экран результата, а не два разных.
 */
export function finishMistakes(
  runtime: Runtime,
  workspace: Workspace,
  session: MistakesSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectMistakeOutcomes(session);
  const statsAfter = applyOutcomes(profile.units, profile.stats, outcomes, CONFIG, now);

  let next = upsertMastery(workspace, [], statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: profile.units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'mistakes' }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: 'my-mistakes',
      language: profile.language,
      layoutId: profile.keyboard.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.errors,
      isControlled: false,
    }),
  };
}
